/**
 * The fine height function (decision 11; WORKBOARD hand-offs: lane A builds it, lane C's walk,
 * bullets and sight read it). At k_human = 1/20 the planet mesh's facets are ~4 person-heights
 * across: to a person the ground is boulders and gentle slopes, with nothing to lie behind. The
 * fix is one height function with two resolutions — the mesh for everything vehicle-sized, and
 * the mesh plus a person-scale micro-relief for every person-side query.
 *
 * Rules:
 * - **Bumps only.** The fine surface = the mesh + an offset in [0, amplitude]. So anything the mesh
 *   blocks stays blocked, and the person queries are the mesh queries plus one extra test.
 * - **Off at classic.** The amplitude comes from the scale profile (PersonSet.fineAmplitude) and is
 *   0 for `classic`, so every classic answer is bit-identical; the turn match never calls these.
 * - **The same on every engine.** Value noise on an integer lattice with Math.imul hashing and a
 *   smoothstep blend: + − × and floor only (no transcendental functions), so Node and Chrome agree
 *   to the bit.
 * - **Stable.** Seeded by the planet's id, radius and archetype, not its heightmap: a crater changes
 *   the mesh under the hummocks, not the hummocks.
 * - **Walkable.** Two octaves (the profile's wavelength, and a third of it at a quarter of the height)
 *   keep the steepest micro-slope near 0.4 — under the walk's 1.2 limit.
 *
 * Rendering the micro-relief near the camera is the renderer's (a local patch over the mesh that
 * samples `fineOffset`); the WASM kernel does not need it yet (the action clock's sight and bullets
 * are JS). If person-side queries move into the kernel, this file is what lib.rs mirrors.
 */

import { craterletShape, CRATERLET_REACH } from './craterlets';
import { standGround } from './standGround';
import { bumpIndex, type BumpIndex } from './bumpIndex';
import { terraceGround, terraceLocal, terraceReach, TERRACE } from './terraceGround';
import { smokeBlocks, smokeArm } from './smoke';
import type { Craterlet, Dig, GameState, PlanetState, PlayerState, Vec3 } from '../types';
import { digGeometry, DIG_REACH, DIG_STEP_PEOPLE, type DigGeometry } from './dig';
import { dcos } from './dmath';
import { boundLook, boundWith, displacedTriangle, getSurfaceRadiusRaw, getTerrainIndex, lastSurfaceTriangle, look, rayWellInside, surfaceRadiusLook, surfaceTables, type SurfaceTables } from './surface';
import { hasLineOfSight, eyePointAt, losEyePoint, abovePoint } from './los';
import { lineUnderGround } from './horizonCull';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { MAX_TERRAIN_HEIGHT_FRACTION } from '../constants';
import { simWork } from './workCounters';
import { eyeScaleOf } from './stance';
import { leanOffset } from './lean';
import { wallsAcross } from './hits';   // (69.15: a cycle by design — hits reads the fine ground; nothing of either runs at load)

/** A stable per-planet seed: id, radius bits and archetype — never the (crater-scarred) heightmap. */
export function fineSeed(planet: Pick<PlanetState, 'id' | 'radius' | 'archetype'>): number {
  let h = Math.imul(planet.id + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(Math.floor(planet.radius * 1024), 0xc2b2ae35);
  const a = planet.archetype ?? '';
  for (let i = 0; i < a.length; i++) h = Math.imul(h ^ a.charCodeAt(i), 0x27d4eb2f);
  return h | 0;
}

const seedMemo = new WeakMap<object, number>();

/**
 * 62.64: a lattice cell's eight corner values, hashed into here (c = x + 2y + 4z for the corner's offsets) — a module
 * buffer, so no value crosses a call between the hash and the blend.
 */
const corner = new Float64Array(8);

/**
 * The micro-relief above the mesh at a unit direction on `planet`: in [0, amplitude]. 0 when the
 * amplitude is 0 (classic).
 *
 * Two octaves of trilinear value noise on an integer lattice (the broad wavelength at 0.8, a third of it at 0.2), each
 * lattice point's value in [0, 1) from its Math.imul hash, blended by a smoothstep. 62.64: computed here in place — a
 * double passed to or returned from a call TurboFan does not inline is boxed (a helper per octave and per corner made
 * about six heap numbers a lookup, ~0.8 MB a tick at 32 a side); the same operations in the same order, so the same bits.
 */
export function fineOffset(planet: Pick<PlanetState, 'id' | 'radius' | 'archetype'>, ux: number, uy: number, uz: number, amplitude: number, wavelength: number): number {
  fineIn[0] = ux; fineIn[1] = uy; fineIn[2] = uz; fineIn[3] = amplitude; fineIn[4] = wavelength;
  fineOffsetIn(planet);
  return fineIn[5];
}

/**
 * 62.64: fineOffset's way in and out for the hot callers (a sight line's samples, a person's ground): the unit direction
 * [0..2], the amplitude [3] and the wavelength [4] in, the offset out [5] — a module buffer, not five double arguments and
 * a return (each boxed across a call TurboFan does not inline). fineOffset is this, wrapped.
 */
const fineIn = new Float64Array(6);

/** fineOffset over `fineIn`: [0..4] in, [5] out. */
function fineOffsetIn(planet: Pick<PlanetState, 'id' | 'radius' | 'archetype'>): void {
  const ux = fineIn[0], uy = fineIn[1], uz = fineIn[2], amplitude = fineIn[3], wavelength = fineIn[4];
  if (!(amplitude > 0) || !(wavelength > 0)) { fineIn[5] = 0; return; }
  let seed = seedMemo.get(planet);   // 62.51: a planet object's seed never changes (a crater makes a new object)
  if (seed === undefined) { seed = fineSeed(planet); seedMemo.set(planet, seed); }
  // lattice coordinates: the surface point in units of each octave's cell
  const broad = planet.radius / wavelength, fine = broad * 3;
  let n = 0;
  for (let o = 0; o < 2; o++) {
    const f = o === 0 ? broad : fine, key = o === 0 ? seed : seed ^ 0x5bd1e995;
    const x = ux * f, y = uy * f, z = uz * f;
    const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
    const fx = x - x0, fy = y - y0, fz = z - z0;
    const tx = fx * fx * (3 - 2 * fx), ty = fy * fy * (3 - 2 * fy), tz = fz * fz * (3 - 2 * fz);
    // 62.51: each lattice corner hashed once
    for (let c = 0; c < 8; c++) {
      let h = Math.imul(x0 + (c & 1), 0x8da6b343) ^ Math.imul(y0 + ((c >> 1) & 1), 0xd8163841) ^ Math.imul(z0 + (c >> 2), 0xcb1ab31f) ^ key;
      h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
      h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
      h ^= h >>> 15;
      corner[c] = (h >>> 0) / 4294967296;
    }
    const x00 = corner[0] + (corner[1] - corner[0]) * tx;
    const x10 = corner[2] + (corner[3] - corner[2]) * tx;
    const x01 = corner[4] + (corner[5] - corner[4]) * tx;
    const x11 = corner[6] + (corner[7] - corner[6]) * tx;
    const y0v = x00 + (x10 - x00) * ty;
    const y1v = x01 + (x11 - x01) * ty;
    const v = y0v + (y1v - y0v) * tz;
    n = o === 0 ? 0.8 * v : n + 0.2 * v;
  }
  fineIn[5] = amplitude * n;
}

/**
 * 66.8 (ARCHITECTURE §5.18): the digs' share of the ground (u) at the unit direction (ux, uy, uz) on `planet` — each dig's
 * heights (core/dig.ts) in its own frame (z toward its `facing`, x across), in person units × the person (`k`). 0 on a
 * planet with none. A dig is person-scale ground: it is read where the fine ground is (every person's reader takes the fine
 * ground then — off, people stand on the mesh, and nothing is dug: core/entrench.ts).
 */
export function digHeightAt(planet: Pick<PlanetState, 'digs' | 'radius'>, ux: number, uy: number, uz: number, k: number): number {
  const digs = planet.digs;
  if (!digs || digs.length === 0) return 0;
  const unit = (PERSON_BODY_HEIGHT * k) / 1.8;   // person units → world
  const cosReach = dcos((DIG_REACH * unit) / planet.radius);
  // 63.11 step 1: only the digs filed in this point's cell (bumpIndex: every one whose reach the cell meets, in the array's
  // order) — the same sum, term for term, as the whole list's
  const near = bumpIndex(digs, planet.radius, DIG_REACH * unit).at(ux, uy, uz);
  let h = 0;
  for (let q = 0; q < near.length; q++) {
    const d = digs[near[q]];
    if (ux * d.dir.x + uy * d.dir.y + uz * d.dir.z < cosReach) continue;
    const g = digGeometryOf(d);
    if (!g) continue;
    // (the offset in the tangent plane at the dig — small angles: the chord is the arc to far below a person's width)
    const ox = ux - d.dir.x, oy = uy - d.dir.y, oz = uz - d.dir.z;
    const rx = d.facing.y * d.dir.z - d.facing.z * d.dir.y, ry = d.facing.z * d.dir.x - d.facing.x * d.dir.z, rz = d.facing.x * d.dir.y - d.facing.y * d.dir.x;
    const z = ((ox * d.facing.x + oy * d.facing.y + oz * d.facing.z) * planet.radius) / unit;
    const x = ((ox * rx + oy * ry + oz * rz) * planet.radius) / unit;
    h += g.height(x, z) * unit;
  }
  return h;
}
/** 63.11: a planet's craterlets as flat arrays beside their cell index (built once per list): centre, reach², bowl, depth, rim. */
interface CraterletTable { index: BumpIndex; toPeople2: number; x: Float64Array; y: Float64Array; z: Float64Array; out2: Float64Array; out: Float64Array; bowl: Float64Array; depth: Float64Array; rim: Float64Array }
const craterletTables = new WeakMap<readonly unknown[], { radius: number; person: number; t: CraterletTable }>();
function craterletTable(list: readonly Craterlet[], radius: number, person: number): CraterletTable {
  const kept = craterletTables.get(list);
  if (kept && kept.radius === radius && kept.person === person) return kept.t;
  const n = list.length, f = () => new Float64Array(n);
  const t: CraterletTable = { index: bumpIndex(list, radius, CRATERLET_REACH * person), toPeople2: (radius / person) * (radius / person), x: f(), y: f(), z: f(), out2: f(), out: f(), bowl: f(), depth: f(), rim: f() };
  for (let i = 0; i < n; i++) {
    const c = list[i], s = craterletShape(c);
    t.x[i] = c.dir.x; t.y[i] = c.dir.y; t.z[i] = c.dir.z;
    t.out[i] = s?.rimOut ?? 0; t.out2[i] = t.out[i] * t.out[i]; t.bowl[i] = s?.radius ?? 0; t.depth[i] = s?.depth ?? 0; t.rim[i] = s?.rim ?? 0;
  }
  craterletTables.set(list, { radius, person, t });
  return t;
}

/**
 * 63.11 (§5.18): the craterlets' share of the ground (u) at the unit direction (ux, uy, uz) on `planet` — each one's bowl and
 * rim (core/craterlets.ts) by its distance, in person heights × the person; through the same cell index as the digs.
 */
export function craterletHeightAt(planet: Pick<PlanetState, 'craterlets' | 'radius'>, ux: number, uy: number, uz: number, k: number): number {
  const list = planet.craterlets;
  if (!list || list.length === 0) return 0;
  const person = PERSON_BODY_HEIGHT * k;
  const t = craterletTable(list, planet.radius, person);
  const near = t.index.at(ux, uy, uz);
  let h = 0;
  for (let q = 0; q < near.length; q++) {
    const i = near[q];
    // (the chord in person heights: the arc to far below a person; each one's shape read from the table's flat arrays)
    const ox = ux - t.x[i], oy = uy - t.y[i], oz = uz - t.z[i];
    const r2 = (ox * ox + oy * oy + oz * oz) * t.toPeople2;
    if (r2 >= t.out2[i]) continue;
    const r = Math.sqrt(r2), b = t.bowl[i];
    if (r < b) { const qq = r / b; h -= t.depth[i] * (1 - qq * qq) * person; }
    else { const u = (r - b) / (t.out[i] - b); h += t.rim[i] * 4 * u * (1 - u) * person; }
  }
  return h;
}
const digGeometries = new WeakMap<Dig, DigGeometry | null>();
function digGeometryOf(d: Dig): DigGeometry | null {
  let g = digGeometries.get(d);
  if (g === undefined) { g = digGeometry(d.kind, d.material, d.done); digGeometries.set(d, g); }
  return g;
}

/** (66.23: a ceiling with the very inputs it was taken from — the planet's ground arrays by identity, the relief's dials) */
interface Ceiling { heightmap: Float32Array; basePositions: Float32Array; radius: number; digs: unknown; craterlets: unknown; terraces: unknown; stands: unknown; amp: number; k: number | undefined; ceil: number;
  /** 69.67 (groundCeilingNear): what stands over the coarse mesh anywhere (the relief's amplitude, the digs' spoil, the craterlets' rims), and the highest terrace or deck (−∞: none) */
  over: number; level: number }
const ceilings = new WeakMap<PlanetState, Ceiling>();
/**
 * 66.23: a radius no person's ground on `planet` reaches above (personSurfaceRadius ≤ it everywhere) — the coarse mesh's
 * highest corner (a point of a flat facet stands no farther out than its farthest corner; the lookup's fallback, radius + h,
 * taken too), the micro-relief's amplitude (its offset lies in [0, amplitude]), each dig's spoil (its banks, at most 2.5 × its
 * lip's top: dig.ts) and each craterlet's rim, all summed; then a terrace's level and a stand's deck where higher (each takes
 * the ground to its own height or keeps it). A flight above it has not met the ground, so a ground test asks no lookup there:
 * the same answers. Memoised by the planet and checked against every input it reads, by identity — its heightmap and base
 * positions, its digs', craterlets', terraces' and stands' lists, the radius and the relief's dials (a crater, a dig, a
 * craterlet, a build or a stand seated each makes new ones: none is edited in place) — so a stale ceiling cannot stand.
 */
export function groundCeiling(planet: PlanetState, ps: GroundDials): number {
  return ceilingOf(planet, ps).ceil;
}

/**
 * 69.67: groundCeiling over the mesh's bin under a direction (x, y, z: the direction from the planet's centre, need not be
 * unit) — the bin's highest corner (core/surface.ts boundWith: the bin the lookup reads) with what stands over the mesh
 * anywhere, a terrace's level or a deck where higher, and never above the planet's own. A strike's round flies under the
 * planet's highest ground most of its way (a megaplanet's mountains) but well over the ground under it: here its ground
 * test asks no lookup either — the same answers.
 */
export function groundCeilingNear(planet: PlanetState, ps: GroundDials): (x: number, y: number, z: number) => number {
  const c = ceilingOf(planet, ps);
  if (!(c.ceil < Infinity) || planet.heightmap.length === 0 || planet.basePositions.length !== planet.heightmap.length * 3) return () => c.ceil;
  const t = surfaceTables(planet.heightmap, planet.basePositions, planet.radius), R = planet.radius;
  return (x, y, z) => Math.min(c.ceil, Math.max(boundWith(t, R, x, y, z) + c.over, c.level) * (1 + 1e-9) + 1e-9);
}

function ceilingOf(planet: PlanetState, ps: GroundDials): Ceiling {
  const amp = ps.fineAmplitude > 0 ? ps.fineAmplitude : 0, kept = ceilings.get(planet);
  if (kept && kept.amp === amp && kept.k === ps.k && kept.heightmap === planet.heightmap && kept.basePositions === planet.basePositions && kept.radius === planet.radius
    && kept.digs === planet.digs && kept.craterlets === planet.craterlets && kept.terraces === planet.terraces && kept.stands === planet.stands) return kept;
  const hm = planet.heightmap, bp = planet.basePositions;
  let top = planet.radius;
  if (hm.length > 0 && bp.length === hm.length * 3) {
    const inv = getTerrainIndex(bp).invLen;
    for (let i = 0; i < hm.length; i++) {
      const x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2];
      const r = Math.max(Math.sqrt(x * x + y * y + z * z) * (1 + hm[i] * inv[i]), planet.radius + hm[i]);
      if (!(r >= 0)) { top = Infinity; break; }   // (ground the lookup cannot read: no ceiling)
      if (r > top) top = r;
    }
  } else if (hm.length > 0) top = Infinity;
  let natural = top + amp, over = amp, level = -Infinity;
  if (amp > 0 && ps.k !== undefined) {
    const unit = (PERSON_BODY_HEIGHT * ps.k) / 1.8, person = PERSON_BODY_HEIGHT * ps.k;
    for (const d of planet.digs ?? []) { const g = digGeometryOf(d); if (g) { natural += 2.5 * Math.max(0, g.top) * unit; over += 2.5 * Math.max(0, g.top) * unit; } }
    for (const c of planet.craterlets ?? []) { natural += Math.max(0, craterletShape(c)?.rim ?? 0) * person; over += Math.max(0, craterletShape(c)?.rim ?? 0) * person; }
  }
  let ceil = natural;
  if (amp > 0) {
    for (const t of planet.terraces ?? []) { if (t.level > ceil) ceil = t.level; if (t.level > level) level = t.level; }
    for (const st of planet.stands ?? []) { if (st.top > ceil) ceil = st.top; if (st.top > level) level = st.top; }
  }
  ceil = ceil * (1 + 1e-9) + 1e-9;   // (float slack)
  const out: Ceiling = { heightmap: hm, basePositions: bp, radius: planet.radius, digs: planet.digs, craterlets: planet.craterlets, terraces: planet.terraces, stands: planet.stands, amp, k: ps.k, ceil, over, level };
  ceilings.set(planet, out);
  return out;
}

/**
 * 69.72 step 2 (ARCHITECTURE §5.33 item 2): the ground a person stands on, kept per planet object for the directions
 * asked lately — the walk, the hull test, the frames, the bullets and the bots ask the same spot many times a tick (44–61 %
 * of a tick's lookups on the fight board), and a unit standing still asks it again every tick. The answer is a function of
 * the planet object (every ground edit makes a new one) and the exact numbers asked (Object.is, so -0 is not 0), so a kept
 * answer is the very number a lookup makes. A small table a planet, a slot by the direction's bits; a collision replaces.
 */
const GROUND_SLOTS = 1024, KEY = 7;
interface GroundKept { key: Float64Array; value: Float64Array; filled: Uint8Array }
const groundKept = new WeakMap<PlanetState, GroundKept>();
const bitsF = new Float64Array(1), bitsU = new Uint32Array(bitsF.buffer);
function slotOf(x: number, y: number, z: number): number {
  bitsF[0] = x; let h = Math.imul(bitsU[0] ^ bitsU[1], 0x9e3779b1);
  bitsF[0] = y; h = Math.imul(h ^ bitsU[0] ^ bitsU[1], 0x85ebca6b);
  bitsF[0] = z; h = Math.imul(h ^ bitsU[0] ^ bitsU[1], 0xc2b2ae35);
  return (h ^ (h >>> 15)) & (GROUND_SLOTS - 1);
}

/**
 * 71 step 0 (ARCHITECTURE §5.36, DECISIONS 324): the levels of the one ground a reader asks for, in place of a partial set of
 * dials or a planet copied without its stands. `full`: the mesh, the relief, the digs and craterlets, the terraces and the
 * stands' decks — what a person stands on. `ground`: the same without the decks (a footing's rim, a skirt). `coarse`: the
 * mesh alone (the far ground; what the classic game and the kernel read).
 */
export type GroundLevel = 'full' | 'ground' | 'coarse';
/** The dials every ground query takes — all three, so no reader leaves out the digs (§5.36's finding 1: k was optional). */
export type GroundDials = Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k'>;
const LEVEL_CODE: Record<GroundLevel, number> = { full: 0, ground: 1, coarse: 2 };

/** Ground radius a person stands on: the mesh plus the micro-relief (the mesh alone at classic), and the digs (66.8) — the one ground's `full` level. */
export function personSurfaceRadius(planet: PlanetState, dir: Vec3, ps: GroundDials): number { return groundAt(planet, dir, 'full', ps); }

/** 71 step 0: the one ground at `level` (GroundLevel), kept as 69.72 keeps it — the level is part of the key. */
export function groundAt(planet: PlanetState, dir: Vec3, level: GroundLevel, ps: GroundDials): number {
  let kept = groundKept.get(planet);
  if (!kept) { kept = { key: new Float64Array(GROUND_SLOTS * KEY), value: new Float64Array(GROUND_SLOTS), filled: new Uint8Array(GROUND_SLOTS) }; groundKept.set(planet, kept); }
  const slot = slotOf(dir.x, dir.y, dir.z), o = slot * KEY, key = kept.key, lv = LEVEL_CODE[level];
  if (kept.filled[slot] && Object.is(key[o], dir.x) && Object.is(key[o + 1], dir.y) && Object.is(key[o + 2], dir.z)
    && Object.is(key[o + 3], ps.fineAmplitude) && Object.is(key[o + 4], ps.fineWavelength) && Object.is(key[o + 5], ps.k) && key[o + 6] === lv) return kept.value[slot];
  const r = groundRadius(planet, dir, level, ps);
  key[o] = dir.x; key[o + 1] = dir.y; key[o + 2] = dir.z; key[o + 3] = ps.fineAmplitude; key[o + 4] = ps.fineWavelength; key[o + 5] = ps.k; key[o + 6] = lv;
  kept.value[slot] = r; kept.filled[slot] = 1;
  return r;
}

/** The lookup itself (groundAt keeps its answers). */
function groundRadius(planet: PlanetState, dir: Vec3, level: GroundLevel, ps: GroundDials): number {
  simWork.personGroundLookups++;   // 62.53: counted for the frame budget (69.72 step 2: the lookups made, not those kept)
  const dl = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  // (62.64: the lookups over their buffers — getSurfaceRadiusRaw and fineOffset, the same numbers without boxing them)
  look[0] = dir.x; look[1] = dir.y; look[2] = dir.z; look[3] = planet.radius;
  surfaceRadiusLook(getTerrainIndex(planet.basePositions), planet.heightmap, planet.basePositions);
  const coarse = look[4];
  if (level === 'coarse' || !(ps.fineAmplitude > 0) || dl < 1e-12) return coarse;
  let dug = planet.digs?.length ? digHeightAt(planet, dir.x / dl, dir.y / dl, dir.z / dl, ps.k) : 0;
  if (planet.craterlets?.length) dug += craterletHeightAt(planet, dir.x / dl, dir.y / dl, dir.z / dl, ps.k);   // 63.11
  fineIn[0] = dir.x / dl; fineIn[1] = dir.y / dl; fineIn[2] = dir.z / dl; fineIn[3] = ps.fineAmplitude; fineIn[4] = ps.fineWavelength;
  fineOffsetIn(planet);
  // 63.14 2b: a terrace's pad and its batter band (core/terraceGround.ts) — flat to the bit inside, the natural ground held to the batter round it
  const natural = planet.terraces?.length ? terraceGround(planet.terraces, planet.radius, fineIn[0], fineIn[1], fineIn[2], coarse + fineIn[5] + dug) : coarse + fineIn[5] + dug;
  // (209's follow-up: a structure's deck where it stands higher — core/standGround.ts)
  return level === 'full' && planet.stands?.length ? standGround(planet.stands, planet.radius, fineIn[0], fineIn[1], fineIn[2], natural, planet.standCells) : natural;
}

/**
 * 66.8 (§5.18): where segment a→b first goes under a dug ground (its berm, its far wall) — the stretch within reach of each
 * dig sampled every DIG_STEP_PEOPLE person heights (a berm is narrower than the sight march's step or a bullet's substep).
 * `t` along the segment (0 … 1); null when it meets no dig's ground. Only the digs' ground: the rest is the callers'.
 */
export function digSegmentHit(a: Vec3, b: Vec3, planets: readonly PlanetState[], ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k'>): { t: number; planetIndex: number; point: Vec3 } | null {
  let best: { t: number; planetIndex: number; point: Vec3 } | null = null;
  const unit = (PERSON_BODY_HEIGHT * ps.k) / 1.8, step = DIG_STEP_PEOPLE * PERSON_BODY_HEIGHT * ps.k, reach = DIG_REACH * unit;
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z, len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-12) return null;
  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.digs?.length || !planet.alive) continue;
    for (const d of planet.digs) {
      // (the dig's ground point; the stretch of the segment within reach of it, and a person above it)
      const gx = planet.position.x + d.dir.x * planet.radius, gy = planet.position.y + d.dir.y * planet.radius, gz = planet.position.z + d.dir.z * planet.radius;
      const tc = ((gx - a.x) * dx + (gy - a.y) * dy + (gz - a.z) * dz) / (len * len);
      const cx = a.x + dx * tc - gx, cy = a.y + dy * tc - gy, cz = a.z + dz * tc - gz;
      const off = Math.sqrt(cx * cx + cy * cy + cz * cz), r = reach + PERSON_BODY_HEIGHT * ps.k;
      if (off > r) continue;
      const half = Math.sqrt(Math.max(0, r * r - off * off)) / len;
      const t0 = Math.max(0, tc - half), t1 = Math.min(1, tc + half);
      if (t1 <= t0 || (best && t0 >= best.t)) continue;
      const n = Math.max(1, Math.ceil(((t1 - t0) * len) / step));
      for (let j = 0; j <= n; j++) {
        const t = t0 + ((t1 - t0) * j) / n;
        if (best && t >= best.t) break;
        const px = a.x + dx * t - planet.position.x, py = a.y + dy * t - planet.position.y, pz = a.z + dz * t - planet.position.z;
        const dist = Math.sqrt(px * px + py * py + pz * pz);
        if (dist < 1e-12) continue;
        if (dist <= personSurfaceRadius(planet, { x: px, y: py, z: pz }, ps)) {
          const kk = personSurfaceRadius(planet, { x: px, y: py, z: pz }, ps) / dist;
          best = { t, planetIndex: pi, point: { x: planet.position.x + px * kk, y: planet.position.y + py * kk, z: planet.position.z + pz * kk } };
          break;
        }
      }
    }
  }
  return best;
}

/** How far apart the samples across a terrace's band are (people): the cut face rises 2.5 over 1.7, the bank over 5. */
const TERRACE_STEP_PEOPLE = 0.5;
/** The stretches of the last line checked that lie inside a pad (pairs of t): the march skips them — a pad's ground is its level, which the pad test has answered; the mesh and the relief under it (a cut pad's, above it) are not ground. */
const terraceSkips: number[] = [];
let terraceSkipCount = 0;
/**
 * 63.14 2b: whether segment a→b goes under a terrace's ground (core/terraceGround.ts) — the sight march reads the mesh and
 * the relief, which a pad's level and its batter band override. Inside a pad the ground is one level: the line's lowest
 * point over its stretch inside the pad against the level, no samples (the pad as a plane: the chord stands in for the arc
 * over a few people). Across the band, the ground sampled TERRACE_STEP_PEOPLE apart (personSurfaceRadius, the rule in it).
 */
export function terraceSegmentBlocked(a: Vec3, b: Vec3, planets: readonly PlanetState[], ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k'>): boolean {
  const person = PERSON_BODY_HEIGHT * ps.k, step = TERRACE_STEP_PEOPLE * person;
  terraceSkipCount = 0;
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z, len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-12) return false;
  for (let pi = 0; pi < planets.length; pi++) {
    const planet = planets[pi];
    if (!planet.terraces?.length || !planet.alive) continue;
    const R = planet.radius;
    for (const t of planet.terraces) {
      // the segment within reach of the terrace (its centre's point, a person over for the eyes)
      const gx = planet.position.x + t.dir.x * R, gy = planet.position.y + t.dir.y * R, gz = planet.position.z + t.dir.z * R;
      const reach = terraceReach(t) + person + TERRACE.maxStepPeople * person;
      const tc = ((gx - a.x) * dx + (gy - a.y) * dy + (gz - a.z) * dz) / (len * len);
      const cx = a.x + dx * tc - gx, cy = a.y + dy * tc - gy, cz = a.z + dz * tc - gz;
      const off = Math.sqrt(cx * cx + cy * cy + cz * cz);
      if (off > reach) continue;
      const half = Math.sqrt(Math.max(0, reach * reach - off * off)) / len;
      const t0 = Math.max(0, tc - half), t1 = Math.min(1, tc + half);
      if (t1 <= t0) continue;
      // the endpoints in the pad's frame (world units from its centre; the frame's axes as terraceLocal's)
      const f = t.facing, d = t.dir;
      const rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;
      const ax = (a.x - gx) * rx + (a.y - gy) * ry + (a.z - gz) * rz, az = (a.x - gx) * f.x + (a.y - gy) * f.y + (a.z - gz) * f.z;
      const bx = (b.x - gx) * rx + (b.y - gy) * ry + (b.z - gz) * rz, bz = (b.x - gx) * f.x + (b.y - gy) * f.y + (b.z - gz) * f.z;
      // the stretch inside the pad (a segment clipped to a rectangle)
      let i0 = t0, i1 = t1;
      for (const [p0, p1, h] of [[ax, bx, t.halfX], [az, bz, t.halfZ]] as const) {
        const dp = p1 - p0;
        if (Math.abs(dp) < 1e-12) { if (Math.abs(p0) > h) { i0 = 1; i1 = 0; } continue; }
        const ta = (-h - p0) / dp, tb = (h - p0) / dp;
        i0 = Math.max(i0, Math.min(ta, tb)); i1 = Math.min(i1, Math.max(ta, tb));
      }
      if (i1 > i0) {
        // the line's lowest radius over the stretch (r² is quadratic in t: its vertex, clamped)
        const px = a.x - planet.position.x, py = a.y - planet.position.y, pz = a.z - planet.position.z;
        const dd = dx * dx + dy * dy + dz * dz, ts = Math.max(i0, Math.min(i1, -(px * dx + py * dy + pz * dz) / dd));
        const qx = px + dx * ts, qy = py + dy * ts, qz = pz + dz * ts;
        if (Math.sqrt(qx * qx + qy * qy + qz * qz) <= t.level) return true;
        terraceSkips[terraceSkipCount++] = i0; terraceSkips[terraceSkipCount++] = i1;   // clear over the pad: the march skips it
      }
      // the band: the stretches within reach and outside the pad, sampled
      const stretches = i1 > i0 ? [[t0, i0], [i1, t1]] : [[t0, t1]];
      for (const [s0, s1] of stretches) {
        if (s1 <= s0) continue;
        const n = Math.max(1, Math.ceil(((s1 - s0) * len) / step));
        for (let j = 0; j <= n; j++) {
          const tt = s0 + ((s1 - s0) * j) / n;
          const px = a.x + dx * tt - planet.position.x, py = a.y + dy * tt - planet.position.y, pz = a.z + dz * tt - planet.position.z;
          const dist = Math.sqrt(px * px + py * py + pz * pz);
          if (dist < 1e-12) continue;
          const l = terraceLocal(t, R, px / dist, py / dist, pz / dist);
          const ex = Math.max(Math.abs(l.x) - t.halfX, 0), ez = Math.max(Math.abs(l.z) - t.halfZ, 0);
          if (ex * ex + ez * ez >= t.band * t.band) continue;   // (beyond the band: the march's own ground)
          if (dist <= personSurfaceRadius(planet, { x: px, y: py, z: pz }, ps)) return true;
        }
      }
    }
  }
  return false;
}

/** The point a person stands on, `height` above the fine ground (feet: 0; eye: ps.losEyeHeight). */
export function personPointAt(planet: PlanetState, dir: Vec3, ps: GroundDials, height = 0): Vec3 {
  const dl = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  if (dl < 1e-12) return { ...planet.position };
  const r = personSurfaceRadius(planet, dir, ps) + height;
  return { x: planet.position.x + dir.x / dl * r, y: planet.position.y + dir.y / dl * r, z: planet.position.z + dir.z / dl * r };
}

/**
 * 62.57's second lever (lane F): a unit's ground, once. Every reader of a unit's place — its eye (sight), its hit centre
 * and body (hits, aim), its muzzle, the fire lanes — asks the fine ground under the same `surfaceDir` object many times a
 * tick (at 50 a side ~11,000 lookups a tick for 100 units). The answer is a pure function of the planet object (a crater
 * makes a new one), the direction's components and the fine ground's two dials, and a unit's `surfaceDir` is never
 * changed in place (a move makes a new object), so it is kept on that object: bit for bit the lookup's answer.
 */
const unitGround = new WeakMap<Vec3, { planet: PlanetState; amp: number; wl: number; k: number; r: number }>();
function unitSurfaceRadius(planet: PlanetState, dir: Vec3, ps: GroundDials): number {
  const m = unitGround.get(dir);
  if (m && m.planet === planet && m.amp === ps.fineAmplitude && m.wl === ps.fineWavelength && m.k === ps.k) return m.r;
  const r = personSurfaceRadius(planet, dir, ps);
  if (m) { m.planet = planet; m.amp = ps.fineAmplitude; m.wl = ps.fineWavelength; m.k = ps.k; m.r = r; }
  else unitGround.set(dir, { planet, amp: ps.fineAmplitude, wl: ps.fineWavelength, k: ps.k, r });
  return r;
}

/** personPointAt for a unit's own place (`dir` its `surfaceDir`): the same point, its ground looked up once (unitSurfaceRadius). */
export function unitPointAt(planet: PlanetState, dir: Vec3, ps: GroundDials, height = 0): Vec3 {
  const dl = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  if (dl < 1e-12) return { ...planet.position };
  const r = unitSurfaceRadius(planet, dir, ps) + height;
  return { x: planet.position.x + dir.x / dl * r, y: planet.position.y + dir.y / dl * r, z: planet.position.z + dir.z / dl * r };
}

/**
 * S3: a point `height` above where a person would stand at `dir` — on the fine ground at person scale, and
 * exactly `eyePointAt` (the mesh) at classic, so classic answers stay bit-identical.
 */
/**
 * 62.65 (d): a point on the person's ground kept for the object that names it — a mark's order, a look's entry — valid while
 * the planet, its heightmap, its digs, its craterlets and the person set are the same objects (every one is copy-on-write:
 * a crater is a new heightmap, a dig or a craterlet a new list, a tuning a new set). The brains asked for a team's marks
 * up to sixteen times a bot a tick and placed every one on the fine ground each time (17 % of the tick at 50 a side with
 * marks on). The value is shared: callers read it, never write into it. Pure: the same numbers whoever asks first.
 */
export interface GroundPointKept { planet: PlanetState; heightmap: Float32Array; digs: unknown; craterlets: unknown; ps: unknown; pos: Vec3 }
export function personPointKept<K extends object>(kept: WeakMap<K, GroundPointKept>, key: K, planet: PlanetState, dir: Vec3, ps: GroundDials, height: number, via: 'on' | 'at' = 'on'): Vec3 {
  const hit = kept.get(key);
  if (hit && hit.planet === planet && hit.heightmap === planet.heightmap && hit.digs === planet.digs && hit.craterlets === planet.craterlets && hit.ps === ps) return hit.pos;
  // `via`: personPointOn (a mark: the classic eye point at classic) or personPointAt (a look's entry: the person's ground at every scale) — the caller's own function, bit for bit
  const pos = via === 'on' ? personPointOn(planet, dir, ps, height) : personPointAt(planet, dir, ps, height);
  kept.set(key, { planet, heightmap: planet.heightmap, digs: planet.digs, craterlets: planet.craterlets, ps, pos });
  return pos;
}

export function personPointOn(planet: PlanetState, dir: Vec3, ps: GroundDials, height: number): Vec3 {
  return ps.fineAmplitude > 0 ? personPointAt(planet, dir, ps, height) : eyePointAt(planet, dir, height);
}

/** S3: a point `height` up a person — where they fly when airborne, else above the fine ground; exactly `losEyePoint` at classic. */
export function personEyePoint(p: PlayerState, planets: PlanetState[], ps: GroundDials, height: number): Vec3 {
  if (!p.airborne && (p.stanceShift || (p.stance && p.stance !== 'stand'))) height *= eyeScaleOf(p);   // 62.2: crouched or prone, lower (216: eased through a change)
  const at = pointUp(p, planets, ps, height);
  // 66.7 lean step 2: leaning out of cover, a point up the body is aside of where the person stands (core/lean.ts)
  const lean = p.cover ? leanOffset(p, ps.k, height) : null;
  return lean ? { x: at.x + lean.x, y: at.y + lean.y, z: at.z + lean.z } : at;
}

/** A point `height` up a person where it stands (or flies, or sits in its hatch) — personEyePoint before any lean. */
function pointUp(p: PlayerState, planets: PlanetState[], ps: GroundDials, height: number): Vec3 {
  if (!(ps.fineAmplitude > 0)) return losEyePoint(p, planets, height);
  if (p.airborne && p.airbornePos) return p.airbornePos;
  if (p.seatPos) return abovePoint(p.seatPos, planets[p.planetIndex], height);   // 63.1d step 2: in its hatch
  return unitPointAt(planets[p.planetIndex], p.surfaceDir, ps, height);
}

/** Is this point inside the fine ground of any planet (the mesh, or a hummock on it)? Returns the planet and the ground point under it. */
export function personGroundHit(pos: Vec3, state: Pick<GameState, 'planets' | 'config'>): { planetIndex: number; point: Vec3 } | null {
  const ps = personSetOf(state);
  for (let i = 0; i < state.planets.length; i++) {
    const planet = state.planets[i];
    if (!planet.alive || planet.type !== 'planet') continue;   // stars and wormholes have no ground to stand on (checkCollision handles them)
    const dx = pos.x - planet.position.x, dy = pos.y - planet.position.y, dz = pos.z - planet.position.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist > planet.radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION) + ps.fineAmplitude || dist < 1e-12) continue;
    const ground = personSurfaceRadius(planet, { x: dx, y: dy, z: dz }, ps);
    if (dist <= ground) {
      const k = ground / dist;
      return { planetIndex: i, point: { x: planet.position.x + dx * k, y: planet.position.y + dy * k, z: planet.position.z + dz * k } };
    }
  }
  return null;
}

function lexLess(a: Vec3, b: Vec3): boolean {
  if (a.x !== b.x) return a.x < b.x;
  if (a.y !== b.y) return a.y < b.y;
  return a.z < b.z;
}

const triScratch = new Float64Array(9);
/** 62.64: personLineOfSight's per-line tables and skips, kept between lines (an array and a typed array a line were made). */
const lineTables: Array<SurfaceTables | null> = [];
let lineSkips = new Int32Array(4);
/**
 * 62.51, the coarse-then-fine step (exact): the line's samples k..m (planet-relative points from + (dx, dy, dz)·j/n) whose
 * rays all stay well inside mesh triangle `tri` (its cone is convex: both ends inside, the whole stretch is) lie over its
 * plane, where the ground's radius along a ray through p is |p|·(N·p0)/(N·p); so the height over the ground is
 * |p|·(N·(p − p0))/(N·p), bounded below by min|p| · min N·(p − p0) / max N·p over the stretch (both linear in j). When
 * that clears the hummocks (`amplitude`) by a margin far past rounding, each of those samples would pass the per-sample
 * test anyway — the line skips them. Returns the last sample known clear (k when none ahead is).
 */
function clearsAhead(planet: PlanetState, tbl: SurfaceTables, tri: number, from: Vec3, dx: number, dy: number, dz: number, k: number, n: number, amplitude: number): number {
  displacedTriangle(planet.heightmap, planet.basePositions, tbl.idx.invLen, tri, triScratch);
  const T = triScratch;
  const e1x = T[3] - T[0], e1y = T[4] - T[1], e1z = T[5] - T[2], e2x = T[6] - T[0], e2y = T[7] - T[1], e2z = T[8] - T[2];
  let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
  let np0 = nx * T[0] + ny * T[1] + nz * T[2];
  if (np0 < 0) { nx = -nx; ny = -ny; nz = -nz; np0 = -np0; }
  if (!(np0 > 0)) return k;
  const ox = from.x - planet.position.x, oy = from.y - planet.position.y, oz = from.z - planet.position.z;
  const margin = 1e-9 * planet.radius + 1e-12;
  for (const step of [256, 64, 16, 4]) {
    const m = Math.min(n - 1, k + step);
    if (m <= k) return k;
    const ta = k / n, tb = m / n;
    const ax = ox + dx * ta, ay = oy + dy * ta, az = oz + dz * ta;
    const bx = ox + dx * tb, by = oy + dy * tb, bz = oz + dz * tb;
    if (!rayWellInside(T, ax, ay, az) || !rayWellInside(T, bx, by, bz)) continue;
    const npa = nx * ax + ny * ay + nz * az, npb = nx * bx + ny * by + nz * bz;
    const ha = npa - np0, hb = npb - np0;
    if (!(ha > 0 && hb > 0)) continue;
    // the stretch's nearest point to the centre
    const sx = bx - ax, sy = by - ay, sz = bz - az, ss = sx * sx + sy * sy + sz * sz;
    const w = ss > 0 ? Math.max(0, Math.min(1, -(ax * sx + ay * sy + az * sz) / ss)) : 0;
    const cx = ax + sx * w, cy = ay + sy * w, cz = az + sz * w;
    const minLen = Math.sqrt(cx * cx + cy * cy + cz * cz);
    const clear = (minLen * Math.min(ha, hb)) / Math.max(npa, npb);
    if (clear > amplitude + margin) return m;
  }
  return k;
}

/**
 * A person's line of sight: the mesh raycast, then the hummocks — sampled along the segment every
 * eighth of the broad wavelength, only where the segment runs within the amplitude of the mesh — and, 69.15, no wall across it.
 * Symmetric (always sampled from the lexicographically smaller end), and exactly `hasLineOfSight`
 * at classic. Endpoints should be person points (`personPointAt`), so an eye is never inside a bump.
 */
export function personLineOfSight(a: Vec3, b: Vec3, state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'smokes' | 'structures' | 'rocks' | 'flora' | 'ruins'>>): boolean {
  if (!personGroundSight(a, b, state)) return false;
  // 69.15 (the owner: "Great, good future item"): and no wall across it (core/hits.ts wallsAcross: the buildings, the wrecks,
  // the containers and frames, the ruins) — game-wide, every sight that asks with the structures (a caller without them: the
  // ground's answer)
  return !(state.structures && state.rocks && state.flora && wallsAcross(a, b, state as Parameters<typeof wallsAcross>[2]));
}

/** The ground's half of a person's line of sight (personLineOfSight without the walls): the mesh, smoke, digs, terraces, the relief. */
function personGroundSight(a: Vec3, b: Vec3, state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'smokes'>>): boolean {
  simWork.personSightLines++;   // 62.53: counted for the frame budget
  // 62.59: a line under the planet's lowest ground for longer than a march step is blocked — the march's answer, unmarched
  if (lineUnderGround(a, b, state.planets)) { simWork.sightCulled++; return false; }
  // 62.60 (the look's cost; GameConfig.sightMarch): at a fine scale the march below samples the mesh's own ground every
  // eighth of the broad wavelength (0.75 people) and blocks under it as under the hummocks, so the exact mesh raycast before
  // it — 8 % of a fight's tick — is asked only without the switch; with it, a crest thinner than a sample's stride between
  // two samples is the one thing the raycast saw that the march may not, and a hollow of the relief under a mesh face lets
  // a line through that the raycast stopped (the fine ground is the ground people stand on). At classic (no relief) the
  // raycast is the whole answer, switch or not. Off: every byte as before
  if (!(state.config.sightMarch && personSetOf(state).fineAmplitude > 0) && !hasLineOfSight(a, b, state.planets)) return false;
  // 65.15: a standing smoke cloud is opaque to everyone — people, bots, drones and towers alike; 65.24 (smokeSight): past
  // arm's length only, so people in one cloud see each other
  if (state.smokes && smokeBlocks(state.smokes, a, b)) {
    const arm = smokeArm(state);
    const ex = b.x - a.x, ey = b.y - a.y, ez = b.z - a.z;
    if (!(arm > 0) || Math.sqrt(ex * ex + ey * ey + ez * ez) > arm) return false;
  }
  const ps = personSetOf(state);
  terraceSkipCount = 0;
  // 66.8: a dig's ground (its berm, its far wall), sampled finely where the line passes one
  if (ps.fineAmplitude > 0 && state.planets.some(p => p.digs?.length) && digSegmentHit(lexLess(a, b) ? a : b, lexLess(a, b) ? b : a, state.planets, ps)) return false;
  // 63.14 2b: a terrace's pad (its level, in one) and its batter band (its cut face, its fill bank: sampled), where the line passes one
  if (ps.fineAmplitude > 0 && state.planets.some(p => p.terraces?.length) && terraceSegmentBlocked(lexLess(a, b) ? a : b, lexLess(a, b) ? b : a, state.planets, ps)) return false;
  if (!(ps.fineAmplitude > 0)) return true;
  const from = lexLess(a, b) ? a : b, to = from === a ? b : a;
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const n = Math.max(1, Math.ceil(len / (ps.fineWavelength / 8)));
  // 62.51: each planet's terrain index and bound table fetched once for the line, not once a sample
  const planets = state.planets;
  // (62.64: the line's tables and skips in buffers kept between lines — filled for this line's planets, never shortened)
  const tables = lineTables;
  for (let pi = 0; pi < planets.length; pi++) { const pl = planets[pi]; tables[pi] = pl.alive ? surfaceTables(pl.heightmap, pl.basePositions, pl.radius) : null; }
  // 62.51: per planet, the samples up to which the line is known to clear its ground by more than the hummocks (skipUntil)
  if (lineSkips.length < planets.length) lineSkips = new Int32Array(planets.length);
  const skipUntil = lineSkips;
  skipUntil.fill(0, 0, planets.length);
  const skips = terraceSkipCount;   // (63.14 2b: the stretches inside a pad the terrace test cleared — their ground is the level, not the mesh and relief under it)
  for (let k = 1; k < n; k++) {
    const t = k / n;
    if (skips > 0) { let inPad = false; for (let q = 0; q < skips; q += 2) if (t >= terraceSkips[q] && t <= terraceSkips[q + 1]) { inPad = true; break; } if (inPad) continue; }
    const px = from.x + dx * t, py = from.y + dy * t, pz = from.z + dz * t;
    for (let pi = 0; pi < planets.length; pi++) {
      const planet = planets[pi];
      if (!planet.alive || k <= skipUntil[pi]) continue;
      const rx = px - planet.position.x, ry = py - planet.position.y, rz = pz - planet.position.z;
      const dist = Math.sqrt(rx * rx + ry * ry + rz * rz);
      if (dist > planet.radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION) + ps.fineAmplitude || dist < 1e-12) continue;
      const tbl = tables[pi]!;
      // 62.51: well above the farthest ground this direction's triangles reach — the same answer, without the lookup
      // (62.64: the bound and the lookup over their buffer, not four doubles each across the call)
      look[0] = rx; look[1] = ry; look[2] = rz; look[3] = planet.radius;
      boundLook(tbl);
      if (dist - look[4] > ps.fineAmplitude) continue;
      surfaceRadiusLook(tbl.idx, planet.heightmap, planet.basePositions);
      const coarse = look[4];
      if (dist - coarse > ps.fineAmplitude) {
        // 62.51: well above this triangle — how many samples ahead stay inside it and above it by more than the hummocks?
        const tri = lastSurfaceTriangle();
        if (tri >= 0) skipUntil[pi] = clearsAhead(planet, tbl, tri, from, dx, dy, dz, k, n, ps.fineAmplitude);
        continue;                                                // above every hummock here
      }
      fineIn[0] = rx / dist; fineIn[1] = ry / dist; fineIn[2] = rz / dist; fineIn[3] = ps.fineAmplitude; fineIn[4] = ps.fineWavelength;
      fineOffsetIn(planet);
      if (dist <= coarse + fineIn[5]) return false;
    }
  }
  return true;
}
